/*
 * Copyright (c) 2020-present Amida Technology Solutions, Inc.
 * All rights reserved.
 *
 * Licensed under the Amida proprietary license.
 * See LICENSE file in the project root for license information.
 */

/**
 * NotificationBell — header bell icon that opens a right-side slide-out panel.
 *
 * Uses real API hooks (useNotificationInboxCount, useNotificationInbox)
 * instead of hardcoded data. Polls unread count every 60 s.
 */

import { useState } from "react";
import { Bell, CheckCheck, X } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetDescription,
} from "@/components/ui/sheet";
import {
  useNotificationInbox,
  useNotificationInboxCount,
  useMarkNotificationRead,
  useMarkAllNotificationsRead,
  type NotificationLogItem,
} from "@/api/hooks/useNotifications";
import { mapSeverity, severityStyle, type DisplaySeverity } from "@/lib/alertSeverity";
import { EVENT_CATEGORIES } from "@/pages/admin/Notifications/constants";

/** Build a lookup: event_type code -> human label. */
const EVENT_LABELS: Record<string, string> = {};
for (const cat of Object.values(EVENT_CATEGORIES)) {
  for (const evt of cat.events) {
    EVENT_LABELS[evt.id] = evt.label;
  }
}

function eventLabel(eventType: string): string {
  return EVENT_LABELS[eventType] ?? eventType.replace(/_/g, " ");
}

// Left-accent colour per severity, reusing the shared palette (matches the row icon colour).
// Every bell row is unread/new, so the accent bar doubles as the "new" highlight and encodes level.
const ACCENT_CLASS: Record<DisplaySeverity, string> = {
  critical: "border-l-red-500",
  high: "border-l-amber-500",
  medium: "border-l-yellow-500",
  low: "border-l-blue-500",
};

function NotificationRow({
  item,
  onMarkRead,
}: {
  item: NotificationLogItem;
  onMarkRead: (id: string) => void;
}) {
  const body =
    item.event_data?.["submission_name"] ??
    item.event_data?.["incident_title"] ??
    item.event_data?.["description"] ??
    null;

  // Shared severity → icon/colour mapping, so S0–S3 render identically to the dashboard
  // Recent Alerts panel. Missing/unknown severity falls back to the calm "low" default.
  const severityCode = item.event_data?.["severity"];
  const display = mapSeverity(severityCode);
  const style = severityStyle(severityCode);
  const Icon = style.icon;
  const label = eventLabel(item.event_type);

  // Emphasis mirrors the Recent Alerts panel: only critical (S0) gets a red-tinted background;
  // high-severity (S0 + S1) headings render red. Others stay neutral. The left accent bar +
  // bold heading mark the row as new/unread (the bell only ever lists unread items).
  const isCritical = display === "critical";
  const isHigh = display === "critical" || display === "high";
  const rowBg = isCritical ? "bg-red-50/70 hover:bg-red-50" : "hover:bg-muted/50";
  const titleClass = isHigh ? "text-red-700" : "text-foreground";

  // Dismiss = mark this notification read (there is no delete endpoint; the row is preserved for
  // audit). In the unread-only bell that drops it from the list, so it reads as "remove".
  return (
    <div
      className={`group relative border-b border-l-[3px] border-border ${ACCENT_CLASS[display]} ${rowBg} transition-colors last:border-b-0`}
    >
      <button
        type="button"
        className="flex w-full cursor-pointer items-start gap-2 px-4 py-3 pr-10 text-left"
        onClick={() => {
          if (!item.is_read) onMarkRead(item.id);
        }}
      >
        {/* Icon shape + colour carry severity; role/aria-label give a non-colour cue (WCAG 1.4.1). */}
        <Icon
          className={`mt-0.5 h-4 w-4 shrink-0 ${style.iconClass}`}
          role="img"
          aria-label={`${style.label} severity`}
        />
        <div className="flex min-w-0 flex-1 flex-col gap-1">
          <p className={`text-sm font-semibold ${titleClass}`}>{label}</p>
          {body && <p className="line-clamp-2 text-xs text-muted-foreground">{body}</p>}
          <div className="flex items-center gap-2">
            {severityCode && (
              <span className="rounded-full bg-muted px-1.5 py-0.5 text-[10px] font-semibold uppercase leading-4 tracking-wide text-muted-foreground">
                {severityCode}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {item.sent_at
                ? formatDistanceToNow(new Date(item.sent_at), { addSuffix: true })
                : "-"}
            </span>
          </div>
        </div>
      </button>
      {/* Sibling of the row button (never nested — avoids nested interactive controls, WCAG 4.1.2). */}
      <button
        type="button"
        aria-label={`Dismiss notification: ${label}`}
        className="absolute right-2 top-2 rounded-md p-1 text-muted-foreground transition-colors hover:bg-muted hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
        onClick={() => {
          onMarkRead(item.id);
        }}
      >
        <X className="h-4 w-4" aria-hidden="true" />
      </button>
    </div>
  );
}

const PAGE_SIZE = 20;

export function NotificationBell() {
  const [open, setOpen] = useState(false);

  const { data: countData } = useNotificationInboxCount();
  // Unread-only: reading an item (or "mark all read") drops it from the list. There is no
  // "load more" — the backend inbox returns total = items.length (no real total), so paging
  // could never work correctly; the bell shows the most recent unread and links out for the rest.
  const { data: inboxData } = useNotificationInbox(true, PAGE_SIZE);
  const markRead = useMarkNotificationRead();
  const markAllRead = useMarkAllNotificationsRead();

  const unread = countData?.unread ?? 0;
  const items = inboxData?.items ?? [];

  return (
    <>
      {/* Solid backdrop so the unread badge (offset outside the icon) is not over header blur. */}
      <span className="inline-flex shrink-0 rounded-full bg-background p-2 shadow-sm ring-1 ring-border/40">
        <Button
          variant="ghost"
          size="icon"
          className="relative"
          onClick={() => {
            setOpen(true);
          }}
          aria-label={`Notifications${unread > 0 ? `, ${String(unread)} unread` : ""}`}
        >
          <Bell className="h-4 w-4" />
          {unread > 0 && (
            <Badge
              variant="destructive"
              className="count-pill-destructive absolute -right-1 -top-1 z-10 flex h-5 min-w-5 max-w-[2.75rem] shrink-0 items-center justify-center border-0 px-0.5 py-0 text-xs font-semibold leading-none ring-2 ring-background"
              aria-hidden="true"
            >
              {unread > 99 ? "99+" : unread}
            </Badge>
          )}
        </Button>
      </span>

      <Sheet open={open} onOpenChange={setOpen}>
        <SheetContent side="right" className="flex w-full flex-col p-0 sm:max-w-md">
          <SheetHeader className="flex-row items-center justify-between border-b px-4 py-3">
            <div>
              <SheetTitle className="text-base">Notifications</SheetTitle>
              <SheetDescription className="text-xs">
                {unread > 0 ? `${String(unread)} unread` : "All caught up"}
              </SheetDescription>
            </div>
            {unread > 0 && (
              <Button
                variant="ghost"
                size="sm"
                className="h-8 gap-1.5 text-xs text-muted-foreground"
                onClick={() => {
                  markAllRead.mutate();
                }}
              >
                <CheckCheck className="h-3.5 w-3.5" />
                Mark all read
              </Button>
            )}
          </SheetHeader>

          <div className="flex-1 overflow-y-auto">
            {items.length === 0 ? (
              <div className="flex flex-col items-center justify-center gap-2 px-4 py-16 text-center">
                <Bell className="h-8 w-8 text-muted-foreground/40" />
                <p className="text-sm font-medium">You're all caught up</p>
                <p className="text-xs text-muted-foreground">No unread notifications</p>
              </div>
            ) : (
              items.map((item) => (
                <NotificationRow
                  key={item.id}
                  item={item}
                  onMarkRead={(id) => {
                    markRead.mutate(id);
                  }}
                />
              ))
            )}
          </div>
        </SheetContent>
      </Sheet>
    </>
  );
}
